/**
 * PointLightComponent - emits light uniformly in all directions from a single point.
 * Wraps THREE.PointLight with distance and decay properties.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { LightComponent } from './LightComponent.js';

import type { LightComponentOptions } from './LightComponent.js';


export interface PointLightComponentOptions extends LightComponentOptions {
  /** Distance at which the light's intensity falls to 0 */
  distance?: number;
  /** How quickly the light's intensity diminishes with distance */
  decay?: number;
}

/** Wraps a THREE.PointLight. */
@EngineClass('PointLightComponent')
export class PointLightComponent extends LightComponent<THREE.PointLight, PointLightComponentOptions> {

  @property({ transient: true })
  get distance(): number {
    return this.light?.distance ?? 0;
  }
  set distance(value: number) {
    if (this.light) {
      this.light.distance = value;
    }
    this.recreateLightHelper();
  }

  @property({ transient: true })
  get decay(): number {
    return this.light?.decay ?? 2;
  }
  set decay(value: number) {
    if (this.light) {
      this.light.decay = value;
    }
  }

  protected override createLight(): THREE.PointLight {
    return new THREE.PointLight();
  }

  public override initialize(options?: PointLightComponentOptions): void {
    super.initialize(options);
    this.distance = options?.distance ?? this.distance;
    this.decay = options?.decay ?? this.decay;
  }

  protected override createLightHelper(): THREE.Object3D | null {
    if (this.light) {
      return new THREE.PointLightHelper(this.light, this.light.distance);
    }
    return null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_PointLight';
  }
}
